import { useState } from "react";
import { Button, Select, Tooltip } from "antd";
import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  CopyOutlined,
  DeleteOutlined,
  OrderedListOutlined,
  PlayCircleOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { usePermissionCheck } from "@components/PermissionedComponents";
import { HelpButton } from "@components/Common/HelpButton";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { useDashboard } from "../Contexts/dashboardHooks";
import { DASHBOARD_QUERY_PARAM } from "../Contexts/DashboardContext";
import { BulkTableFiltersButton } from "./BulkTableFiltersModal";
import { DebugFeaturesButton } from "./DebugFeaturesButton";
import { DashboardReorderModal } from "./DashboardReorderModal";
import { useExperimentalFlag } from "./experimentalFlags";
import { confirmDuplicateDashboard, confirmDeleteDashboard } from "./dashboardConfirms";
import { PdfExportControl } from "../PDF/PdfExportControl";

/**
 * The dashboard toolbar: dashboard selector + dashboard-CRUD icon buttons on the
 * left, and export/help/tutorial/add-widget actions on the right. Reads the
 * dashboard context, permissions and experimental flags directly; DashboardContent
 * only passes the handlers tied to its own modal state.
 */
export function DashboardToolbar({
  onRenameDashboard,
  onAddWidget,
  onShowTutorial,
}: {
  onRenameDashboard: () => void;
  onAddWidget: () => void;
  onShowTutorial: () => void;
}) {
  const { state, dashboardList, loadDashboard, createDashboard, duplicateDashboard, deleteDashboard } =
    useDashboard();
  const { permissionDisabled: cannotCreate } = usePermissionCheck("CreateSandboxBoards");
  const { permissionDisabled: cannotEdit } = usePermissionCheck("EditSandboxBoards");
  const { permissionDisabled: cannotDelete } = usePermissionCheck("DeleteSandboxBoards");
  const showReorderMenu = useExperimentalFlag("showComponentReorderingMenu");
  const showLegacyControlPanel = useExperimentalFlag("showLegacyControlPanel");
  const [reorderOpen, setReorderOpen] = useState(false);

  const handleDashboardSwitch = (id: string) => {
    if (id !== state.dashboardId) loadDashboard(id);
  };

  const handleCopyUrl = async () => {
    if (!state.dashboardId) return;
    const url = `${window.location.origin}${window.location.pathname}?${DASHBOARD_QUERY_PARAM}=${state.dashboardId}`;
    try {
      await navigator.clipboard.writeText(url);
      message.success("Dashboard link copied to clipboard");
    } catch {
      message.error("Failed to copy link");
    }
  };

  return (
    <div className="dashboard-toolbar">
      <div className="dashboard-toolbar-left">
        <Select
          value={state.dashboardId}
          onChange={handleDashboardSwitch}
          style={{ width: 220 }}
          placeholder="Select Dashboard"
        >
          {dashboardList.map((d) => (
            <Select.Option key={d.dashboard_id} value={d.dashboard_id}>
              {d.name}
            </Select.Option>
          ))}
        </Select>

        <Tooltip title={cannotEdit ? "You don't have permission to edit dashboards" : "Rename dashboard"}>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={onRenameDashboard}
            disabled={cannotEdit}
            className="dashboard-toolbar-icon-btn"
          />
        </Tooltip>

        <Tooltip title={cannotCreate ? "You don't have permission to create dashboards" : "New dashboard"}>
          <Button
            type="text"
            icon={<PlusOutlined />}
            onClick={createDashboard}
            disabled={cannotCreate}
            className="dashboard-toolbar-icon-btn"
          />
        </Tooltip>

        <Tooltip title="Reload dashboard">
          <Button
            type="text"
            icon={<ReloadOutlined />}
            onClick={() => state.dashboardId && loadDashboard(state.dashboardId)}
            className="dashboard-toolbar-icon-btn"
          />
        </Tooltip>

        <Tooltip title={cannotCreate ? "You don't have permission to create dashboards" : "Duplicate dashboard"}>
          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={() => confirmDuplicateDashboard(state.name, duplicateDashboard)}
            disabled={cannotCreate}
            className="dashboard-toolbar-icon-btn"
          />
        </Tooltip>

        <Tooltip title={cannotDelete ? "You don't have permission to delete dashboards" : "Delete dashboard"}>
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => confirmDeleteDashboard(state.name, deleteDashboard)}
            disabled={cannotDelete}
            className="dashboard-toolbar-icon-btn"
            danger
          />
        </Tooltip>

        {showLegacyControlPanel && <BulkTableFiltersButton />}

        {showReorderMenu && (
          <Tooltip title="Reorder dashboards">
            <Button
              type="text"
              icon={<OrderedListOutlined />}
              onClick={() => setReorderOpen(true)}
              className="dashboard-toolbar-icon-btn"
            />
          </Tooltip>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <HelpButton page="Sandbox" />
        <DebugFeaturesButton />
        <Tooltip title="Copy a shareable link to this dashboard">
          <Button icon={<ShareAltOutlined />} onClick={handleCopyUrl} disabled={!state.dashboardId}>
            Copy URL
          </Button>
        </Tooltip>
        <Button icon={<PlayCircleOutlined />} onClick={onShowTutorial}>
          Show Tutorial
        </Button>
        <PdfExportControl />
        <Tooltip title={cannotEdit ? "You don't have permission to edit dashboards" : undefined}>
          <Button type="primary" onClick={onAddWidget} disabled={cannotEdit}>
            Add Widget
          </Button>
        </Tooltip>
      </div>

      <DashboardReorderModal open={reorderOpen} onClose={() => setReorderOpen(false)} />
    </div>
  );
}
